// Pages CMS service layer
import { supabase } from '@/integrations/supabase/client';

export interface PageSection {
  title?: string;
  subtitle?: string;
  body?: string;
  ctaText?: string;
  ctaUrl?: string;
  [key: string]: string | undefined;
}

export interface PageSections {
  [sectionKey: string]: PageSection;
}

export interface Page {
  id: string;
  slug: string;
  name: string;
  language: string;
  sections: PageSections;
  meta_title: string | null;
  meta_description: string | null;
  is_editable: boolean;
  created_at: string;
  updated_at: string;
}

export interface GetPagesParams {
  search?: string;
  language?: string;
  page?: number;
  pageSize?: number;
}

export interface GetPagesResult {
  data: Page[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// Get all pages with filters and pagination
export async function getPages(params: GetPagesParams = {}): Promise<GetPagesResult> {
  const { search, language, page = 1, pageSize = 20 } = params;

  let query = supabase
    .from('pages')
    .select('*', { count: 'exact' });

  // Apply language filter
  if (language && language !== 'all') {
    query = query.eq('language', language);
  }

  // Apply search filter
  if (search) {
    query = query.or(`name.ilike.%${search}%,slug.ilike.%${search}%`);
  }

  // Sort by name
  query = query.order('slug').order('language');

  // Apply pagination
  const start = (page - 1) * pageSize;
  const end = start + pageSize - 1;
  query = query.range(start, end);

  const { data, error, count } = await query;

  if (error) {
    console.error('Error fetching pages:', error);
    throw error;
  }

  return {
    data: (data || []) as Page[],
    total: count || 0,
    page,
    pageSize,
    totalPages: Math.ceil((count || 0) / pageSize),
  };
}

// Get single page by ID
export async function getPageById(id: string): Promise<Page | null> {
  const { data, error } = await supabase
    .from('pages')
    .select('*')
    .eq('id', id)
    .maybeSingle();

  if (error) {
    console.error('Error fetching page:', error);
    throw error;
  }

  return data as Page | null;
}

// Get page by slug and language (for public frontend)
export async function getPageBySlugAndLanguage(
  slug: string,
  language: string
): Promise<Page | null> {
  const { data, error } = await supabase
    .from('pages')
    .select('*')
    .eq('slug', slug)
    .eq('language', language)
    .maybeSingle();

  if (error) {
    console.error('Error fetching page by slug:', error);
    throw error;
  }

  return data as Page | null;
}

// Update page content and SEO
export async function updatePage(
  id: string,
  updates: Partial<{
    name: string;
    sections: PageSections;
    meta_title: string;
    meta_description: string;
  }>
): Promise<Page> {
  const { data, error } = await supabase
    .from('pages')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating page:', error);
    throw error;
  }

  return data as Page;
}

// Create a new page (admin only)
export async function createPage(pageData: {
  slug: string;
  name: string;
  language: string;
  sections?: PageSections;
  meta_title?: string;
  meta_description?: string;
  is_editable?: boolean;
}): Promise<Page> {
  const { data, error } = await supabase
    .from('pages')
    .insert(pageData)
    .select()
    .single();

  if (error) {
    console.error('Error creating page:', error);
    throw error;
  }

  return data as Page;
}

// Delete page (admin only)
export async function deletePage(id: string): Promise<void> {
  const { error } = await supabase
    .from('pages')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting page:', error);
    throw error;
  }
}

// Get page stats
export async function getPageStats() {
  const { count: totalCount } = await supabase
    .from('pages')
    .select('*', { count: 'exact', head: true });

  const { count: englishCount } = await supabase
    .from('pages')
    .select('*', { count: 'exact', head: true })
    .eq('language', 'en');

  const { count: spanishCount } = await supabase
    .from('pages')
    .select('*', { count: 'exact', head: true })
    .eq('language', 'es');

  const { count: editableCount } = await supabase
    .from('pages')
    .select('*', { count: 'exact', head: true })
    .eq('is_editable', true);

  return {
    total: totalCount || 0,
    english: englishCount || 0,
    spanish: spanishCount || 0,
    editable: editableCount || 0,
  };
}

// Languages
export const PAGE_LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
  { value: 'vi', label: 'Tiếng Việt' },
];

// Common section keys with labels
export const SECTION_LABELS: Record<string, string> = {
  hero: 'Hero Section',
  intro: 'Introduction',
  practiceAreas: 'Practice Areas',
  whyChooseUs: 'Why Choose Us',
  process: 'Our Process',
  services: 'Services',
  attorney: 'Attorney Profile',
  values: 'Our Values',
  commitment: 'Our Commitment',
  credentials: 'Credentials',
  info: 'Information',
  cta: 'Call to Action',
  faq: 'FAQ',
  testimonials: 'Testimonials',
};

// Get section field labels
export const SECTION_FIELD_LABELS: Record<string, string> = {
  title: 'Title',
  subtitle: 'Subtitle',
  body: 'Body Text',
  ctaText: 'Button Text',
  ctaUrl: 'Button URL',
  bio: 'Biography',
  address: 'Address',
  phone: 'Phone Number',
  email: 'Email Address',
};